import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { TrendingUp, Users, CreditCard, Target, Download, Eye, BarChart3 } from 'lucide-react';

const PlanPopularityCard = ({ data, loading = false }) => {
    if (loading) {
        return (
            <Card className="relative overflow-hidden bg-card/50 hover:bg-card/80 transition-all duration-300 border border-border/50 rounded-2xl shadow-sm hover:shadow-md">
                <CardHeader className="pb-2">
                    <CardTitle className="text-base sm:text-lg font-medium text-foreground">Plan Popularity</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                    <div className="animate-pulse space-y-4">
                        <div className="h-4 bg-muted rounded"></div>
                        <div className="h-4 bg-muted rounded w-3/4"></div>
                        <div className="h-4 bg-muted rounded w-1/2"></div>
                    </div>
                </CardContent>
            </Card>
        );
    }

    const { standardCount, proCount, totalPaywallViews, standardConversion, proConversion } = data;
    const totalPurchases = standardCount + proCount;
    const standardPercentage = totalPurchases > 0 ? (standardCount / totalPurchases) * 100 : 0;
    const proPercentage = totalPurchases > 0 ? (proCount / totalPurchases) * 100 : 0;

    return (
        <Card className="group relative overflow-hidden bg-[hsl(var(--card))] text-[hsl(var(--card-foreground))] border-[hsl(var(--border))] hover:border-[hsl(var(--border))]/40 rounded-xl">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <div className="p-2 rounded-lg bg-primary/5 group-hover:bg-primary/10 transition-colors">
                        <BarChart3 className="h-4 w-4 text-primary/50 group-hover:text-primary/80 transition-colors" />
                    </div>
                    <span className="text-foreground/90 font-medium">Plan Popularity</span>
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="space-y-3">
                    {/* Compact Plan Summary */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="text-center space-y-2">
                            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/20 mx-auto">
                                <Download className="w-4 h-4 text-blue-600" />
                            </div>
                            <div className="text-lg font-semibold text-blue-600">{standardCount}</div>
                            <div className="text-xs text-muted-foreground">Standard</div>
                            <div className="text-xs text-muted-foreground">${Math.round(standardConversion)}% conv.</div>
                        </div>

                        <div className="text-center space-y-2">
                            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/20 mx-auto">
                                <CreditCard className="w-4 h-4 text-green-600" />
                            </div>
                            <div className="text-lg font-semibold text-green-600">{proCount}</div>
                            <div className="text-xs text-muted-foreground">Pro</div>
                            <div className="text-xs text-muted-foreground">${Math.round(proConversion)}% conv.</div>
                        </div>
                    </div>

                    {/* Key Metrics */}
                    <div className="pt-2 border-t border-border/50 space-y-2">
                        <div className="flex items-center justify-between text-xs">
                            <span className="text-muted-foreground flex items-center gap-1">
                                <Eye className="w-3 h-3" />
                                Views
                            </span>
                            <span className="font-medium">{totalPaywallViews}</span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                            <span className="text-muted-foreground flex items-center gap-1">
                                <Target className="w-3 h-3" />
                                Conversion
                            </span>
                            <span className="font-medium text-green-600">
                                {totalPaywallViews > 0 ? Math.round((totalPurchases / totalPaywallViews) * 100) : 0}%
                            </span>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

export default PlanPopularityCard; 